/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable prefer-const */
// Removed Dynamsoft dependency in favor of @thewirv/react-barcode-scanner
// import { BarcodeScanner } from 'dynamsoft-javascript-barcode';
import { BarcodeScanner as ReactBarcodeScanner } from '@thewirv/react-barcode-scanner';
import React, { useEffect, useMemo, useState } from 'react';
import './VideoDecode.css';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { CameraIcon } from '@heroicons/react/24/solid';
import { Modal, ModalProps } from '@rabbit/elements/shared-components';
import { useNavigate } from 'react-router-dom';
import { DTVendable } from '@rabbit/data/types';
import { SearchVendablesByUpcCode } from '@rabbit/search/cherchons';
import ROUTE_NAME from '../../utils/url-constants';
import { UserScannerStatusUpdater } from '../../utils/db-action-components';
import { checkCodeScan } from '../../utils/helpers';

// TODO: set proper types for those defined as "Any"
// TODO: Turn all styles in VideoDecode.css into tailwind styles

interface CameraShape {
  deviceId: string;
  label: string;
  _checked: boolean;
}

interface ActiveCameraShape {
  position: number;
  camera: CameraShape;
}

export interface VideoDecodeProps {
  setProductData: React.Dispatch<React.SetStateAction<DTVendable | null>>;
  userStatus: {
    consumerPersonaId: string | null | undefined;
    hasUsedScanner: boolean | undefined;
  };
}

function VideoDecode({ setProductData, userStatus }: VideoDecodeProps) {
  const navigate = useNavigate();

  const [launchStatusUpdater, setLaunchStatusUpdater] = useState(false);
  // React-barcode-scanner doesn't require imperative instance refs
  const [scanLocked, setScanLocked] = useState(false);

  // Modal state, possible types of modals and their settings
  const [showModal, setShowModal] = useState(false);
  const [modalSettings, setModalSettings] = useState<ModalProps>(
    {} as ModalProps
  );

  const modalSettingsOptions: any = {
    start: {
      kind: 'info' as const,
      settings: {
        title: 'Scanning your product',
        text: 'Point your camera at the product barcode or QR code to scan it and search for a match.',
        primaryButtonText: 'Got it',
        handleClose: () => setShowModal(false),
        handlePrimaryClick: async () => await handleFirstTimeUse(),
      },
    },
    searching: {
      kind: 'search' as const,
      settings: {
        text: 'Searching for your product...',
        primaryButtonText: 'Cancel',
        handleClose: () => setShowModal(false),
        handlePrimaryClick: () => setShowModal(false),
      },
    },
    noMatches: {
      kind: 'search' as const,
      settings: {
        text: "We couldn't find your product. Please try again or add your product manually.",
        primaryButtonText: 'Try again',
        outlineButtonText: 'Add manually',
        handleClose: () => window.location.reload(),
        handlePrimaryClick: () => window.location.reload(),
        handleOutlineClick: () => navigate(ROUTE_NAME.PRODUCTS_SEARCH),
      },
    },
    networkError: {
      kind: 'pop-up' as const,
      settings: {
        text: 'Network connection error. Please check your Internet connection and try again.',
        primaryButtonText: 'Continue',
        handleClose: () => navigate('/'),
        handlePrimaryClick: () => window.location.reload(),
      },
    },
    scanningError: {
      kind: 'pop-up' as const,
      settings: {
        text: 'There was an error while scanning. Please try again, making sure the barcode or QR code is in the centre of the frame.',
        primaryButtonText: 'Try again',
        outlineButtonText: 'No, cancel',
        handleClose: () => navigate('/'),
        handlePrimaryClick: () => setShowModal(false),
        handleOutlineClick: () => navigate('/'),
      },
    },
    genericError: {
      kind: 'pop-up' as const,
      settings: {
        text: 'Something went wrong, please try again.',
        primaryButtonText: 'Refresh',
        outlineButtonText: 'Go back',
        handleClose: () => navigate('/'),
        handlePrimaryClick: () => window.location.reload(),
        handleOutlineClick: () => navigate('/'),
      },
    },
  };

  const handleFirstTimeUse = async () => {
    setLaunchStatusUpdater(true);
    setShowModal(false);
  };

  // Camera switching and associated state
  const [activeCamera, setActiveCamera] = useState({} as ActiveCameraShape);
  const [cameraList, setCameraList] = useState([] as CameraShape[]);

  const videoConstraints = useMemo<MediaTrackConstraints>(() => {
    const deviceId = activeCamera?.camera?.deviceId;
    if (deviceId) {
      return { deviceId: { exact: deviceId } };
    }
    return { facingMode: 'environment' };
  }, [activeCamera?.camera?.deviceId]);

  const handleChangeCamera = async () => {
    try {
      if (cameraList.length >= 2) {
        if (activeCamera.position < cameraList.length - 1) {
          setActiveCamera({
            position: activeCamera.position + 1,
            camera: cameraList[activeCamera.position + 1],
          });
        } else {
          setActiveCamera({
            position: 0,
            camera: cameraList[0],
          });
        }
      }
    } catch (ex: any) {
      setModalSettings(modalSettingsOptions.genericError);
      setShowModal(true);
    }
  };

  // Handle searching for product info after detecting barcode

  const handleFindVendable = async (barcode: string) => {
    setModalSettings(modalSettingsOptions.searching);
    setShowModal(true);
    try {
      const searchResults = await SearchVendablesByUpcCode(barcode);
      if (searchResults.numitems > 0) {
        setProductData(searchResults.items[0]);
      } else {
        setModalSettings(modalSettingsOptions.noMatches);
      }
    } catch (err) {
      console.log(err);
      setModalSettings(modalSettingsOptions.genericError);
    }
  };

  // Camera enumeration and selection
  const enumerateCameras = async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices
        .filter((d) => d.kind === 'videoinput')
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || 'Camera',
          _checked: false,
        }));
      if (cams.length === 0) return;
      // Prefer a back camera if available, otherwise last one
      let idx = cams.findIndex((c) => /back|rear|environment/i.test(c.label));
      if (idx < 0) idx = cams.length - 1;
      setCameraList(cams);
      setActiveCamera({ position: idx, camera: cams[idx] });
    } catch (e) {
      console.error('Failed to enumerate cameras:', e);
    }
  };

  const handleCapture = async (payload: string) => {
    if (scanLocked) return;
    const txt = payload;
    if (!txt) return;
    setScanLocked(true);
    try {
      const codeResult = checkCodeScan(txt);
      const finalCode = codeResult ? '9312807' + codeResult : txt;
      await handleFindVendable(finalCode);
    } catch (e) {
      setModalSettings(modalSettingsOptions.scanningError);
      setShowModal(true);
    }
  };

  const handleScanError = (err: unknown) => {
    console.error('Scanner error', err);
    // Show network error if message hints, otherwise generic scanning error
    const msg =
      err instanceof Error
        ? err.message
        : typeof err === 'string'
          ? err
          : '';
    if (/network|permission|denied/i.test(msg)) {
      setModalSettings(modalSettingsOptions.networkError);
    } else {
      setModalSettings(modalSettingsOptions.scanningError);
    }
    setShowModal(true);
  };

  // Init on mount: enumerate cameras and show first-time modal
  useEffect(() => {
    void enumerateCameras();
    if (!userStatus.hasUsedScanner) {
      setModalSettings(modalSettingsOptions.start);
      setShowModal(true);
    }
    // no cleanup necessary for react component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // JSX
  return (
    <div className="absolute inset-0">
      {
        /* A somewhat convoluted way to go about this, but apparently a necessary one - update wouldn't work on Safari otherwise*/
        launchStatusUpdater && userStatus.consumerPersonaId && (
          <UserScannerStatusUpdater personaId={userStatus.consumerPersonaId} />
        )
      }
      <div className="m-auto h-screen max-h-[-webkit-fill-available] overflow-hidden lg:w-1/2 lg:py-4">
        <div className="relative h-full w-full">
          {showModal && (
            <Modal
              kind={modalSettings.kind}
              settings={modalSettings.settings}
            />
          )}
          {/* Scanner component */}
          {ReactBarcodeScanner && (
            <div className="rb-video-container">
              <ReactBarcodeScanner
                constraints={videoConstraints}
                doScan={true}
                onSuccess={handleCapture}
                onError={handleScanError}
                containerStyle={{ height: '100%', width: '100%' }}
                videoContainerStyle={{ height: '100%', width: '100%' }}
                videoStyle={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: 'translateZ(0)',
                }}
              />
            </div>
          )}
          {/* Overlay scan area to mimic previous UI */}
          <div className="pointer-events-none absolute inset-0">
            <svg className="dce-bg-camera" viewBox="0 0 2048 1792">
              <path d="M1024 672q119 0 203.5 84.5t84.5 203.5-84.5 203.5-203.5 84.5-203.5-84.5-84.5-203.5 84.5-203.5 203.5-84.5zm704-416q106 0 181 75t75 181v896q0 106-75 181t-181 75h-1408q-106 0-181-75t-75-181v-896q0-106 75-181t181-75h224l51-136q19-49 69.5-84.5t103.5-35.5h512q53 0 103.5 35.5t69.5 84.5l51 136h224zm-704 1152q185 0 316.5-131.5t131.5-316.5-131.5-316.5-316.5-131.5-316.5 131.5-131.5 316.5 131.5 316.5 316.5 131.5z"></path>
            </svg>
            <div className="dce-scanarea">
              <div className="dce-scanlight"></div>
            </div>
          </div>
          {/* Controls */}
          {cameraList.length > 1 && (
            <div
              onClick={() => handleChangeCamera()}
              className="ripple bg-primary-900 absolute bottom-10 right-6 cursor-pointer rounded-full p-2 text-white"
            >
              <CameraIcon className="h-6 w-6" />
            </div>
          )}
          <div
            className="absolute top-10 right-6 cursor-pointer p-2"
            onClick={() => navigate('/')}
          >
            <XCircleIcon className="h-10 w-10" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Never update UI after mount, dbrjs sdk use native way to bind event, update will remove it.
export default React.memo(VideoDecode, () => true);
